/**
 * Analytics Service
 * 
 * Provides analytics tracking for page loads and user interactions.
 * All tracking is non-blocking and fails silently to not disrupt user experience.
 */

import { supabase } from './supabaseClient';
import { getIPAndGeo, getUserAgent } from './geoService';
import type { Coordinates } from '../types';

/**
 * Generate a UUID v4
 * Falls back to a custom implementation if crypto.randomUUID is not available
 */
function generateUUID(): string {
  // Try native crypto.randomUUID first (most secure)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through to fallback
    }
  }

  // Fallback: Generate UUID v4 manually
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Tracks page load for analytics
 * Saves to database and stores session ID in localStorage to prevent duplicate tracking
 * Fire-and-forget, non-blocking operation
 * 
 * Uses two-step approach: IP first (with fallback), geo second (optional)
 * This ensures IP is almost always captured even if geo lookup fails
 */
export async function trackPageLoad(): Promise<void> {
  // Fire-and-forget analytics, don't block UI
  try {
    // Check if we already tracked this session
    const sessionId = localStorage.getItem('analytics_session_id');
    if (sessionId) {
      return; // Already tracked this session
    }

    // Get IP and geo data with timeout
    // IP is captured separately with fallback (ipify.org → ipapi.co)
    // So it should almost always succeed even if geo lookup fails
    const geoInfoPromise = getIPAndGeo().catch(() => ({
      ip_address: null,
      country: null,
      region: null,
      city: null,
    }));
    
    const geoInfo = await Promise.race([
      geoInfoPromise,
      new Promise<{ ip_address: string | null; country: string | null; region: string | null; city: string | null }>((resolve) =>
        setTimeout(() => resolve({
          ip_address: null,
          country: null,
          region: null,
          city: null,
        }), 7000) // 7s timeout: allows 3s for IP + 3s for geo + 1s buffer
      ),
    ]);

    // Generate session ID
    const newSessionId = generateUUID();
    localStorage.setItem('analytics_session_id', newSessionId);

    // Store in localStorage for later linking to authenticated user
    localStorage.setItem('analytics_data', JSON.stringify({
      session_id: newSessionId,
      ...geoInfo,
      user_agent: getUserAgent(),
      timestamp: new Date().toISOString(),
    }));

    // Save page load to database (anonymous - no user_id yet)
    await supabase
      .from('page_loads')
      .insert({
        session_id: newSessionId,
        user_id: null, // Will be linked when user authenticates
        ip_address: geoInfo.ip_address,
        country: geoInfo.country,
        region: geoInfo.region,
        city: geoInfo.city,
        user_agent: getUserAgent(),
        is_authenticated: false, // Anonymous visitor
      });
  } catch (error) {
    console.error('Failed to track page load:', error);
    // Silent fail - don't disrupt user experience
  }
}

/**
 * Links anonymous page load sessions to authenticated user
 * Called when user signs up or signs in
 * @param userId - Authenticated user ID
 */
export async function linkPageLoadsToUser(userId: string): Promise<void> {
  try {
    // Get session ID from localStorage
    const sessionId = localStorage.getItem('analytics_session_id');
    if (!sessionId) {
      return; // No session to link
    }

    // Update all page loads with this session_id to link to user
    await supabase
      .from('page_loads')
      .update({
        user_id: userId,
        is_authenticated: true,
      })
      .eq('session_id', sessionId)
      .is('user_id', null); // Only update anonymous sessions
  } catch (error) {
    console.error('Failed to link page loads to user:', error);
    // Silent fail - don't disrupt user experience
  }
}

/**
 * Tracks an address search query
 * Logs what user typed in address search
 * 
 * @param userId - Authenticated user ID
 * @param query - Search query text
 */
export async function trackAddressSearch(
  userId: string, 
  query: string
): Promise<void> {
  try {
    await supabase
      .from('address_searches')
      .insert({
        user_id: userId,
        search_query: query,
      });
  } catch (error) {
    console.error('Failed to track address search:', error);
    // Silent fail
  }
}

/**
 * Tracks address selection with coordinates
 * Logs the address user selected from suggestions
 * 
 * @param userId - Authenticated user ID
 * @param address - Selected address string
 * @param coords - Geographic coordinates of selected address
 */
export async function trackAddressSelection(
  userId: string,
  address: string,
  coords: Coordinates
): Promise<void> {
  try {
    // Get location details from reverse geocoding (non-blocking)
    // This tells us WHERE the selected address is located
    const { reverseGeocodeWithDetails } = await import('./geocodingService');
    const locationDetailsPromise = reverseGeocodeWithDetails(coords).catch(() => ({
      address: null,
      country: null,
      region: null,
      city: null,
    }));
    
    const locationDetails = await Promise.race([
      locationDetailsPromise,
      new Promise<{ address: string | null; country: string | null; region: string | null; city: string | null }>((resolve) =>
        setTimeout(() => resolve({ address: null, country: null, region: null, city: null }), 2000)
      ),
    ]);
    
    await supabase
      .from('address_searches')
      .insert({
        user_id: userId,
        search_query: address, // Selected address is also the query result
        selected_address: address,
        latitude: coords.lat,
        longitude: coords.lng,
        country: locationDetails.country,
        region: locationDetails.region,
        city: locationDetails.city,
      });
  } catch (error) {
    console.error('Failed to track address selection:', error);
    // Silent fail
  }
}


